import React, { useEffect, useState, useCallback, useRef } from 'react';
import { X, Loader2, CheckCircle, AlertCircle, FileText, Cpu } from 'lucide-react';
import {
  getContractStatus,
  embedClausesChunked,
  startGCSTransfer,
  extractStructureOrchestrated,
  autoRecoverContract,
  type ContractStatusResponse,
} from '../../services/api';
import { ContractStatus } from '../../lib/contract-status';

export interface ProcessingModalProps {
  isOpen: boolean;
  contractId: string;
  contractName: string;
  initialStatus?: ContractStatus;
  onClose: () => void;
  onComplete?: (status: ContractStatus) => void;
}

type ProcessingPhase = 'starting' | 'processing' | 'completed' | 'failed';
type ProcessingStepType = 'ocr' | 'extracting' | 'storing' | 'embedding' | 'done';

interface ProcessingState {
  phase: ProcessingPhase;
  status: ContractStatus;
  currentStep: ProcessingStepType;
  message: string;
  error?: string;
  startTime: number;
  elapsedTime: number;
  extractionProgress?: number; // 0-100 for two-phase extraction
  extractionDetail?: string; // E.g., "Batch 2 of 4"
  stale?: boolean;
  staleSinceMinutes?: number;
}

const POLL_INTERVAL = 2000; // 2 seconds
const MAX_POLL_DURATION = 900000; // 15 minutes timeout (large PDFs can take 12+ min for OCR)

// Helper to determine if a status is an interrupted/resumable state
const isInterruptedStatus = (status: ContractStatus): boolean => {
  return ['ocr_complete', 'processed', 'extraction_failed', 'processing_failed'].includes(status);
};

export const ProcessingModal: React.FC<ProcessingModalProps> = ({
  isOpen,
  contractId,
  contractName,
  initialStatus,
  onClose,
  onComplete,
}) => {
  const [state, setState] = useState<ProcessingState>({
    phase: 'starting',
    status: 'uploaded',
    currentStep: 'ocr',
    message: 'Initializing document processing...',
    startTime: Date.now(),
    elapsedTime: 0,
  });

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const structureExtractionTriggered = useRef<boolean>(false);
  const gcsTransferTriggered = useRef<boolean>(false);
  const lastTransferTriggerTime = useRef<number>(0); // Timestamp of last transfer trigger
  const startTimeRef = useRef<number>(Date.now());
  const isPollingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup function for intervals
  const cleanupIntervals = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
  }, []);

  // Trigger structure extraction after OCR completes
  // Uses two-phase orchestrated extraction for better performance and no timeouts
  const triggerStructureExtraction = useCallback(async () => {
    if (structureExtractionTriggered.current) {
      return; // Already triggered
    }
    structureExtractionTriggered.current = true;

    // Abort any previous extraction and create a new controller
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setState(prev => ({
        ...prev,
        currentStep: 'extracting',
        message: 'Starting AI structure extraction...',
        extractionProgress: 0,
      }));

      console.log('[ProcessingModal] Starting two-phase structure extraction for:', contractId);

      // Use orchestrated two-phase extraction (avoids timeouts)
      const structure = await extractStructureOrchestrated(contractId, {
        onProgress: (phase, progress, detail) => {
          console.log(`[ProcessingModal] Extraction progress: ${phase} (${progress}%) - ${detail || ''}`);
          setState(prev => ({
            ...prev,
            currentStep: 'extracting',
            message: phase,
            extractionProgress: progress,
            extractionDetail: detail,
          }));
        },
        batchSize: 5, // Extract 5 articles in parallel
        storeStructure: true, // Store in Supabase automatically
        signal: controller.signal,
      });

      console.log('[ProcessingModal] Two-phase extraction complete:', structure.totalClauses, 'clauses');

      // Structure is already stored, proceed to chunked embeddings
      setState(prev => ({
        ...prev,
        currentStep: 'embedding',
        message: 'Generating clause embeddings for semantic matching...',
        extractionProgress: undefined,
        extractionDetail: undefined,
      }));

      let embeddingSucceeded = false;
      try {
        const embedResult = await embedClausesChunked(
          contractId,
          (embedded, total) => {
            setState(prev => ({
              ...prev,
              message: `Embedding clauses... ${embedded}/${total}`,
              extractionProgress: Math.round((embedded / total) * 100),
            }));
          },
          controller.signal,
        );
        embeddingSucceeded = embedResult.complete;
        if (embeddingSucceeded) {
          console.log('[ProcessingModal] Embeddings generated:', embedResult.totalEmbedded, 'clauses');
        } else {
          console.warn('[ProcessingModal] Embedding incomplete:', embedResult.error,
            `(${embedResult.totalEmbedded}/${embedResult.totalClauses} embedded)`);
        }
      } catch (embedError) {
        if (embedError instanceof DOMException && embedError.name === 'AbortError') throw embedError;
        // Log but don't fail - embeddings are optional
        console.warn('[ProcessingModal] Embedding generation failed (non-fatal):', embedError);
      }

      // Done — report actual status
      const finalStatus = embeddingSucceeded ? 'embedded' : 'structured';
      const finalMessage = embeddingSucceeded
        ? 'Document fully processed and ready for analysis!'
        : 'Structure extracted. Embeddings incomplete — use Pipeline Viewer to retry.';

      setState(prev => ({
        ...prev,
        phase: 'completed',
        status: finalStatus,
        currentStep: 'done',
        message: finalMessage,
        extractionProgress: undefined,
        extractionDetail: undefined,
      }));

      cleanupIntervals();
      if (onComplete) {
        onComplete(finalStatus);
      }
    } catch (error) {
      // Silently ignore abort errors (modal was closed/reopened)
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('[ProcessingModal] Extraction aborted (modal closed or reopened)');
        return;
      }
      console.error('[ProcessingModal] Structure extraction error:', error);
      setState(prev => ({
        ...prev,
        phase: 'failed',
        message: error instanceof Error ? error.message : 'Failed to extract structure',
        error: error instanceof Error ? error.message : 'Unknown error',
        extractionProgress: undefined,
        extractionDetail: undefined,
      }));
      cleanupIntervals();
    }
  }, [contractId, cleanupIntervals, onComplete]);

  // Note: completeStructureProcessing removed - orchestrated extraction handles everything

  // Poll for status updates - uses refs to avoid dependency issues
  const pollStatus = useCallback(async () => {
    // Prevent concurrent polls
    if (isPollingRef.current) {
      return;
    }
    isPollingRef.current = true;

    try {
      const response: ContractStatusResponse = await getContractStatus(contractId);
      const currentStatus = response.status as ContractStatus;

      // Determine phase and message based on status
      let phase: ProcessingPhase = 'processing';
      let message = 'Processing document...';
      let currentStep: ProcessingStepType = 'ocr';

      switch (currentStatus) {
        case 'uploaded':
          phase = 'starting';
          message = 'Preparing to process document...';
          currentStep = 'ocr';
          break;
        case 'processing':
          phase = 'processing';
          if (response.stale) {
            message = `Processing appears stuck (${response.staleSinceMinutes ?? '?'}m). Click Recover to restart.`;
          } else {
            message = 'Extracting text with Google Document AI...';
          }
          currentStep = 'ocr';
          break;
        case 'ocr_complete':
          // OCR done in GCP, need to transfer results from GCS to R2
          phase = 'processing';
          message = 'Downloading OCR results...';
          currentStep = 'ocr';
          if (response.needsTransferToR2 && !gcsTransferTriggered.current) {
            gcsTransferTriggered.current = true;
            console.log('[ProcessingModal] Starting GCS-to-R2 transfer for:', contractId);
            startGCSTransfer(contractId).then(result => {
              console.log('[ProcessingModal] GCS-to-R2 transfer result:', result);
              if (!result.success) {
                console.error('[ProcessingModal] GCS-to-R2 transfer failed:', result.error);
                // Reset flag so next poll can retry
                gcsTransferTriggered.current = false;
              }
            }).catch(err => {
              console.error('[ProcessingModal] GCS-to-R2 transfer error:', err);
              // Reset flag so next poll can retry
              gcsTransferTriggered.current = false;
            });
          }
          break;
        case 'transferring_to_r2':
          // OCR results being downloaded from GCS and stored in R2 using chunked processing
          phase = 'processing';
          if (response.transferProgress) {
            const { current, total } = response.transferProgress;
            message = `Transferring file ${current} of ${total} from GCS to R2...`;
          } else {
            message = 'Preparing to transfer OCR results...';
          }
          currentStep = 'ocr';
          // Chunked transfer: trigger next chunk, using timestamp-based debouncing
          // Only skip if we triggered within the last 3 seconds (to avoid hammering during slow transfers)
          const timeSinceLastTrigger = Date.now() - lastTransferTriggerTime.current;
          const shouldTriggerTransfer = timeSinceLastTrigger > 3000;
          console.log('[ProcessingModal] transferring_to_r2 - timeSinceLastTrigger:', timeSinceLastTrigger, 'shouldTrigger:', shouldTriggerTransfer);
          if (shouldTriggerTransfer) {
            lastTransferTriggerTime.current = Date.now();
            console.log('[ProcessingModal] Triggering next chunk of GCS-to-R2 transfer for:', contractId);
            startGCSTransfer(contractId).then(result => {
              console.log('[ProcessingModal] GCS-to-R2 chunk transfer result:', result);
            }).catch(err => {
              console.error('[ProcessingModal] GCS-to-R2 chunk transfer error:', err);
            });
          }
          break;
        case 'processed':
          // OCR complete - trigger two-phase orchestrated structure extraction
          // This runs entirely in the frontend, so we stop polling
          phase = 'processing';
          message = 'Text extraction complete. Starting structure extraction...';
          currentStep = 'extracting';
          // Trigger orchestrated extraction (handles everything including embeddings)
          // Once triggered, we stop polling - the extraction handles its own state
          triggerStructureExtraction();
          // Stop polling since orchestrated extraction manages the rest
          cleanupIntervals();
          return; // Exit early - don't update state from poll anymore
        case 'extracting':
          // Legacy: Structure extraction in progress (old async background task)
          // With two-phase extraction, we shouldn't hit this status
          phase = 'processing';
          message = 'Extracting contract structure with AI...';
          currentStep = 'extracting';
          break;
        case 'extraction_complete':
          // Legacy: Extraction done via old method
          // With two-phase extraction, we shouldn't hit this status
          phase = 'processing';
          message = 'Structure extraction complete. Processing...';
          currentStep = 'storing';
          break;
        case 'extraction_failed':
          // Extraction failed (could happen with old method or if two-phase fails on server)
          phase = 'failed';
          message = response.extractionError || 'Structure extraction failed. Please try again.';
          currentStep = 'extracting';
          break;
        case 'structured':
          phase = 'completed';
          message = 'Document fully processed and ready for analysis!';
          currentStep = 'done';
          break;
        case 'embedded':
          phase = 'completed';
          message = 'Embeddings generated!';
          currentStep = 'done';
          break;
        case 'linked':
          phase = 'completed';
          message = 'Knowledge graph created!';
          currentStep = 'done';
          break;
        case 'ready':
          phase = 'completed';
          message = 'Document fully processed and ready for analysis!';
          currentStep = 'done';
          break;
        case 'error':
          phase = 'failed';
          message = 'Processing failed. Please try again.';
          break;
        case 'processing_failed':
          phase = 'failed';
          if (response.transferProgress) {
            const { current, total } = response.transferProgress;
            message = `Transfer failed on file ${current} of ${total}. Please try again.`;
          } else {
            message = 'Processing failed. Please try again.';
          }
          break;
        default:
          // Handle any unknown status that might contain 'fail'
          if ((currentStatus as string)?.includes('fail')) {
            phase = 'failed';
            message = 'Processing failed. Please try again.';
          }
      }

      // Update state based on current status
      setState(prev => ({
        ...prev,
        phase,
        status: currentStatus,
        currentStep,
        message,
        stale: response.stale,
        staleSinceMinutes: response.staleSinceMinutes,
      }));

      // Stop polling if completed or failed
      if (phase === 'completed' || phase === 'failed') {
        cleanupIntervals();
        if (onComplete) {
          onComplete(currentStatus);
        }
      }

      // Check for timeout using ref instead of state
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed > MAX_POLL_DURATION && phase !== 'completed' && phase !== 'failed') {
        setState(prev => ({
          ...prev,
          phase: 'failed',
          message: 'Processing timed out. The document may still be processing in the background.',
          error: 'timeout',
        }));
        cleanupIntervals();
      }
    } catch (error) {
      console.error('Error polling status:', error);
      // Don't stop polling on error - could be transient
    } finally {
      isPollingRef.current = false;
    }
  }, [contractId, cleanupIntervals, onComplete, triggerStructureExtraction]);

  // Store pollStatus in a ref to avoid dependency issues
  const pollStatusRef = useRef(pollStatus);
  pollStatusRef.current = pollStatus;

  // Start polling when modal opens
  useEffect(() => {
    if (isOpen && contractId) {
      // Abort any previous extraction pipeline
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Reset state and refs
      structureExtractionTriggered.current = false;
      gcsTransferTriggered.current = false;
      isPollingRef.current = false;
      const now = Date.now();
      startTimeRef.current = now;

      // Determine initial message based on whether we're resuming
      const isResuming = initialStatus && isInterruptedStatus(initialStatus);
      let initialMessage = 'Initializing document processing...';
      let initialStep: ProcessingStepType = 'ocr';

      if (isResuming) {
        switch (initialStatus) {
          case 'ocr_complete':
            initialMessage = 'Resuming: Downloading OCR results...';
            initialStep = 'ocr';
            break;
          case 'processed':
            initialMessage = 'Resuming: Starting structure extraction...';
            initialStep = 'extracting';
            break;
          case 'extraction_failed':
            initialMessage = 'Retrying structure extraction...';
            initialStep = 'extracting';
            break;
          case 'processing_failed':
            initialMessage = 'Retrying document processing...';
            initialStep = 'ocr';
            break;
        }
      }

      setState({
        phase: 'starting',
        status: initialStatus || 'uploaded',
        currentStep: initialStep,
        message: initialMessage,
        startTime: now,
        elapsedTime: 0,
      });

      // Start polling immediately (use ref to avoid stale closure)
      pollStatusRef.current();

      // Set up polling interval
      pollIntervalRef.current = setInterval(() => {
        pollStatusRef.current();
      }, POLL_INTERVAL);

      // Set up elapsed time counter
      elapsedIntervalRef.current = setInterval(() => {
        setState(prev => ({
          ...prev,
          elapsedTime: Date.now() - startTimeRef.current,
        }));
      }, 1000);
    }

    return () => {
      cleanupIntervals();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [isOpen, contractId, initialStatus, cleanupIntervals]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && (state.phase === 'completed' || state.phase === 'failed')) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, state.phase]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Format elapsed time
  const formatElapsedTime = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Only allow closing on completed or failed states
    if (e.target === e.currentTarget && (state.phase === 'completed' || state.phase === 'failed')) {
      onClose();
    }
  }, [onClose, state.phase]);

  if (!isOpen) return null;

  const isFinished = state.phase === 'completed' || state.phase === 'failed';

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="processing-modal-title"
      role="dialog"
      aria-modal="true"
      data-testid="processing-modal"
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-neutral-900/50 backdrop-blur-sm transition-opacity ${!isFinished ? 'cursor-not-allowed' : ''}`}
        onClick={handleBackdropClick}
        data-testid="processing-modal-backdrop"
      />

      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-auto animate-in fade-in zoom-in-95 duration-200"
          data-testid="processing-modal-content"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-neutral-100">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                state.phase === 'failed' ? 'bg-red-100' :
                state.phase === 'completed' ? 'bg-success-100' :
                'bg-primary-100'
              }`}>
                {state.phase === 'failed' ? (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                ) : state.phase === 'completed' ? (
                  <CheckCircle className="h-5 w-5 text-success-600" />
                ) : (
                  <Cpu className="h-5 w-5 text-primary-600" />
                )}
              </div>
              <h2
                id="processing-modal-title"
                className="text-lg font-semibold text-neutral-900"
                data-testid="processing-modal-title"
              >
                {state.phase === 'failed' ? 'Processing Failed' :
                 state.phase === 'completed' ? 'Processing Complete' :
                 'Processing Document'}
              </h2>
            </div>
            {isFinished && (
              <button
                onClick={onClose}
                className="btn-icon-sm btn-ghost text-neutral-400 hover:text-neutral-600"
                data-testid="processing-modal-close"
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Document info */}
            <div className="flex items-center gap-3 mb-6 p-3 bg-neutral-50 rounded-lg">
              <FileText className="h-5 w-5 text-neutral-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-neutral-700 truncate" title={contractName}>
                  {contractName}
                </p>
                <p className="text-xs text-neutral-500">
                  {state.status === 'uploaded' ? 'Waiting to process' :
                   state.status === 'processing' ? 'Currently processing' :
                   `Status: ${state.status}`}
                </p>
              </div>
            </div>

            {/* Status indicator */}
            <div className="text-center mb-6">
              {state.phase === 'starting' || state.phase === 'processing' ? (
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <Loader2 className="h-12 w-12 text-primary-500 animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-6 w-6 bg-primary-100 rounded-full" />
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-neutral-600">{state.message}</p>
                  {state.extractionDetail && (
                    <p className="mt-1 text-xs text-neutral-500">{state.extractionDetail}</p>
                  )}
                  {state.extractionProgress !== undefined && (
                    <div className="mt-3 w-full max-w-xs">
                      <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 transition-all duration-300 ease-out"
                          style={{ width: `${state.extractionProgress}%` }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-neutral-400">
                        {state.extractionProgress}% complete
                      </p>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-neutral-400">
                    Elapsed: {formatElapsedTime(state.elapsedTime)}
                  </p>
                  {state.stale && (
                    <button
                      className="mt-3 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-md transition-colors"
                      onClick={async () => {
                        setState(prev => ({ ...prev, message: 'Recovering...', stale: false }));
                        try {
                          const result = await autoRecoverContract(contractId);
                          if (result.success) {
                            console.log('[ProcessingModal] Auto-recover succeeded:', result);
                            // Continue polling — status should update on next poll
                          } else {
                            setState(prev => ({ ...prev, phase: 'failed', message: result.error || 'Recovery failed' }));
                          }
                        } catch (err) {
                          console.error('[ProcessingModal] Auto-recover error:', err);
                          setState(prev => ({ ...prev, phase: 'failed', message: 'Recovery request failed' }));
                        }
                      }}
                    >
                      Recover
                    </button>
                  )}
                </div>
              ) : state.phase === 'completed' ? (
                <div className="flex flex-col items-center">
                  <div className="h-12 w-12 bg-success-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-success-500" />
                  </div>
                  <p className="mt-4 text-sm font-medium text-success-700">{state.message}</p>
                  <p className="mt-2 text-xs text-neutral-400">
                    Completed in {formatElapsedTime(state.elapsedTime)}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                  <p className="mt-4 text-sm font-medium text-red-700">{state.message}</p>
                  {state.error && state.error !== 'timeout' && (
                    <p className="mt-2 text-xs text-red-500">{state.error}</p>
                  )}
                </div>
              )}
            </div>

            {/* Progress steps */}
            <div className="space-y-3">
              <ProcessingStep
                label="Upload"
                isComplete={true}
                isActive={false}
                isFailed={false}
              />
              <ProcessingStep
                label="Text Extraction (OCR)"
                isComplete={['processed', 'structured', 'embedded', 'linked', 'ready'].includes(state.status) || state.currentStep !== 'ocr'}
                isActive={state.currentStep === 'ocr' && state.status === 'processing'}
                isFailed={state.phase === 'failed' && state.currentStep === 'ocr'}
              />
              <ProcessingStep
                label="Structure Extraction (AI)"
                isComplete={['structured', 'embedded', 'linked', 'ready'].includes(state.status) || state.currentStep === 'storing' || state.currentStep === 'embedding' || state.currentStep === 'done'}
                isActive={state.currentStep === 'extracting'}
                isFailed={state.phase === 'failed' && state.currentStep === 'extracting'}
              />
              <ProcessingStep
                label="Clause Embeddings"
                isComplete={['embedded', 'linked', 'ready'].includes(state.status) || state.currentStep === 'done'}
                isActive={state.currentStep === 'embedding'}
                isFailed={state.phase === 'failed' && state.currentStep === 'embedding'}
              />
              <ProcessingStep
                label="Ready for Analysis"
                isComplete={['embedded', 'linked', 'ready'].includes(state.status) || state.currentStep === 'done'}
                isActive={state.currentStep === 'storing'}
                isFailed={state.phase === 'failed' && state.currentStep === 'storing'}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="form-actions p-5 border-t border-neutral-100 bg-neutral-50/50 rounded-b-2xl">
            {!isFinished ? (
              <p className="text-xs text-neutral-500 text-center w-full">
                Please wait while we process your document. Batch extraction typically takes 15-30 minutes.
              </p>
            ) : (
              <button
                onClick={onClose}
                className={`${state.phase === 'completed' ? 'btn-primary' : 'btn-outline'}`}
                data-testid="processing-modal-done"
              >
                {state.phase === 'completed' ? 'Done' : 'Close'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface ProcessingStepProps {
  label: string;
  isComplete: boolean;
  isActive: boolean;
  isFailed: boolean;
}

const ProcessingStep: React.FC<ProcessingStepProps> = ({ label, isComplete, isActive, isFailed }) => {
  return (
    <div className="flex items-center gap-3">
      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
        isFailed ? 'bg-red-100' :
        isComplete ? 'bg-success-100' :
        isActive ? 'bg-primary-100' :
        'bg-neutral-100'
      }`}>
        {isFailed ? (
          <AlertCircle className="h-3.5 w-3.5 text-red-600" />
        ) : isComplete ? (
          <CheckCircle className="h-3.5 w-3.5 text-success-600" />
        ) : isActive ? (
          <Loader2 className="h-3.5 w-3.5 text-primary-600 animate-spin" />
        ) : (
          <div className="h-2 w-2 bg-neutral-300 rounded-full" />
        )}
      </div>
      <span className={`text-sm ${
        isFailed ? 'text-red-600' :
        isComplete ? 'text-neutral-700' :
        isActive ? 'text-primary-700 font-medium' :
        'text-neutral-400'
      }`}>
        {label}
      </span>
    </div>
  );
};

export default ProcessingModal;
